import { useMemo, useState } from 'react';
import { IconBuildingWarehouse, IconCheck, IconExternalLink, IconFileSearch, IconInfoCircle, IconSparkles, IconChevronDown, IconCircleDashed, IconDeviceFloppy, IconFlag, IconMap2, IconPackage, IconPlus, IconUpload, IconUser, IconFileText } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { PageHeader } from '@hana/shell';
import { Button, Input, Segmented, Select, StateView, Textarea, useToast, type ScreenState } from '@hana/ui';
import { KIND_LABEL, START_OPTIONS, isRequired, missingFields, sectionsFor, type FormField, type FormValues, type JobKind } from './newForm';
import { CHAT_SAMPLE, chatChips, dupsOf, parseChat } from './chatRead';
import './jobNewI18n';
import s from './jobNew.module.css';

// Mint-JobNew (desktop 1536): chọn loại job + cách bắt đầu, form theo mục, cột phải tóm tắt + kiểm trước khi tạo. Chưa ghi (D-05).
// Chưa làm: Ask HANA đọc mail, kiểm trùng, copy từ job, đặt nhiều booking, chọn chuyến theo lịch tàu, đính kèm, "Tạo xong tạo tiếp".

const SEC_ICON: Record<string, typeof IconUser> = { cus: IconUser, bk: IconBuildingWarehouse, route: IconMap2, cargo: IconPackage, svc: IconFlag };
const NEXT_JOB_NO = 'SE260639600'; // board: số dự kiến (chưa ghi, chỉ hiển thị)
// Multi Book (Mint-JobNew start=multi): chuyến mẫu của board (hãng, tàu, ETD, cut-off CY, service, sắp quá hạn).
const MB: ReadonlyArray<readonly [string, string, string, string, string, boolean]> = [
  ['KMT', 'SAWASDEE VEGA 2613S', '24/Oct', '24/Oct 06:30', 'VTS1', true],
  ['YML', 'YM CERTAINTY 089N', '24/Oct', '23/Oct 23:59', 'PAS', true],
  ['ONE', 'ONE ALTAIR 076E', '25/Oct', '22/Oct 20:00', 'EC4', true],
  ['MSK', 'MAERSK PORT KLANG 643N', '25/Oct', '23/Oct 02:00', 'CV7', false],
  ['COS', 'SINAR SANUR 152S', '24/Oct', '23/Oct 06:00', 'IHX', false],
  ['KMT', 'KMTC PUSAN 2612S', '25/Oct', '23/Oct 23:59', 'KVT', false],
];
const MB_SRC = [
  { key: 'sys', label: 'Lịch hệ thống' },
  { key: 'ext', label: 'Lịch ngoài' },
  { key: 'clip', label: 'Clipboard' },
];
const afterOf = (k: JobKind): Array<[string, string]> =>
  k === 'ls'
    ? [['SVC', 'Mở việc dịch vụ cho OPS theo loại'], ['DOC', 'Tạo thư mục chứng từ của job']]
    : [['RQ', 'Gửi booking request cho hãng (nếu Pre-booking)'], ['CFM', 'Việc xác nhận booking cho Booking team'], ['SI', 'Hạn nộp SI theo cut-off'], ['DOC', 'Tạo thư mục chứng từ của job']];

const KINDS = (Object.keys(KIND_LABEL) as JobKind[]).map((k) => ({ key: k, label: KIND_LABEL[k] }));

function readStart(): string {
  const v = new URLSearchParams(location.search).get('start');
  // Cổng ảnh nối `?fx=1` sau route đã có `?start=…` → giá trị thành `multi?fx=1`; lấy phần đầu.
  const k = (v ?? '').split('?')[0];
  return k === 'chat' || k === 'multi' ? k : 'blank';
}

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

export function JobNewPage() {
  const t = useT();
  const toast = useToast();
  const forced = readState();
  const [kind, setKind] = useState<JobKind>('sea');
  const [start, setStart] = useState(readStart);
  const [chatMsg, setChatMsg] = useState(CHAT_SAMPLE);
  const [mbN, setMbN] = useState<Record<number, number>>({});
  const [mbSrc, setMbSrc] = useState('sys');
  const [chatP, setChatP] = useState<Record<string, string> | null>(null);
  const [v, setV] = useState<FormValues>({});
  const [tried, setTried] = useState(false);
  const [shut, setShut] = useState<Record<string, boolean>>({});
  const toggle = (k: string) => setShut((c) => ({ ...c, [k]: !c[k] }));

  const sections = sectionsFor(kind);
  const missing = useMemo(() => missingFields(kind, v), [kind, v]);
  const missKeys = new Set(missing.map((f) => f.key));
  const set = (key: string, val: string) => setV((c) => ({ ...c, [key]: val }));
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const create = () => {
    setTried(true);
    if (isMulti && !mbTotal) toast({ msg: 'Chọn ít nhất một chuyến để book' });
    else if (missing.length === 0) noWrite();
    else toast({ msg: `Còn thiếu ${missing.length} ô bắt buộc` });
  };
  const pickStart = (k: string) => {
    if (k !== 'blank' && k !== 'chat' && k !== 'multi') toast({ msg: 'Chưa mở ở bản này' });
    else setStart(k);
  };

  const chatRun = () => {
    const o = parseChat(chatMsg);
    setChatP(o);
    toast({ msg: `Ask HANA đọc được ${Object.keys(o).length} trường · kiểm trùng ${dupsOf(o).length} khớp` });
  };
  const chatFill = () => {
    const f = chatP ?? parseChat(chatMsg);
    setChatP(f);
    setV((c) => ({ ...c, ...f }));
    toast({ msg: `Đã mở form điền sẵn từ chat · ${Object.keys(f).length} ô` });
  };
  const chatDups = chatP ? dupsOf(chatP) : [];
  const mbSet = (i: number, n: number) => setMbN((c) => ({ ...c, [i]: Math.max(0, Math.min(20, n)) }));
  const mbTotal = Object.values(mbN).reduce((a, x) => a + (x || 0), 0);
  const isMulti = start === 'multi' && kind === 'sea';

  const state: ScreenState = forced ?? 'ready';
  const summary: Array<[string, string]> = [
    ['Job dự kiến', isMulti ? `${mbTotal} job · chung batch` : NEXT_JOB_NO],
    ['Loại', KIND_LABEL[kind]],
    ['Khách', v.cus ?? ''],
    ['Tuyến', [v.pol, v.pod].filter(Boolean).map((x) => (x as string).split(' · ')[0]).join(' → ')],
    [kind === 'air' ? 'Hãng bay' : 'Hãng', (kind === 'air' ? v.airl : v.carrier) ?? ''],
    ['Hàng', kind === 'ls' ? (v.lsmode ?? '') : [v.vol, v.tcont].filter(Boolean).join('×') || (v.comm ?? '')],
    ['ShipmentStatus', kind === 'ls' ? 'LS Job' : (v.bstatus || 'RQ Request')],
    ['Phụ trách', [v.sale, v.cs, v.bkg].filter(Boolean).join(' · ')],
  ];

  const field = (f: FormField) => {
    const req = isRequired(f, v);
    const error = tried && missKeys.has(f.key) ? 'Bắt buộc' : undefined;
    const val = v[f.key] ?? '';
    const span = { gridColumn: `span ${f.span}` };
    if (f.type === 'sel')
      return (
        <div key={f.key} className={s.cell} style={span}>
          <Select label={f.label} required={req} error={error} value={val} onChange={(e) => set(f.key, e.target.value)} options={[{ value: '', label: start === 'multi' ? '—' : t('jobNew.selPh') }, ...(f.opts ?? [])]} />
        </div>
      );
    if (f.type === 'seg')
      return (
        <div key={f.key} className={s.cell} style={span}>
          <div className={s.lbl}>
            {f.label} {req && <span className={s.req}>*</span>}
          </div>
          <div className={s.segWrap}><Segmented kind="toggle" label={f.label} value={val} onChange={(k) => set(f.key, k)} items={(f.opts ?? []).map((o) => ({ key: o, label: o }))} /></div>
          {error && (
            <div className={s.err} role="alert">
              {error}
            </div>
          )}
        </div>
      );
    if (f.type === 'chk')
      return (
        <label key={f.key} className={`${s.cell} ${s.chk}`} style={span}>
          <input type="checkbox" checked={val === '1'} onChange={(e) => set(f.key, e.target.checked ? '1' : '')} />
          {f.hint}
        </label>
      );
    if (f.type === 'area')
      return (
        <div key={f.key} className={s.cell} style={span}>
          <Textarea label={f.label} required={req} error={error} placeholder={f.hint} value={val} onChange={(e) => set(f.key, e.target.value)} rows={3} />
        </div>
      );
    return (
      <div key={f.key} className={s.cell} style={span}>
        <Input label={f.label} required={req} error={error} placeholder={f.hint} value={val} onChange={(e) => set(f.key, e.target.value)} />
      </div>
    );
  };

  return (
    <>
      <PageHeader
        title={t('jobNew.a11y1')}
        actions={
<Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={create}>
              {isMulti ? `Tạo ${mbTotal || ''} booking` : 'Tạo job'}
            </Button>
        }
      />
      <div className={s.page}>
        <StateView state={state} title={state === 'empty' ? 'Ghế hiện tại chưa được tạo job' : undefined} desc={state === 'noperm' ? 'Ghế của bạn không tạo được job.' : undefined}>
          <div className={s.bar}>
            <Segmented kind="toggle" label="Loại job" value={kind} onChange={(k) => { setKind(k); setTried(false); }} items={KINDS} />
            <span className={s.barLbl}>{t('jobNew.from')}</span>
            <Segmented
              label="Bắt đầu từ"
              value={start}
              onChange={pickStart}
              items={START_OPTIONS.filter((o) => kind === 'sea' || o.key !== 'multi').map((o) => ({ key: o.key, label: o.label }))}
            />
          </div>
          <div className={s.cols}>
            <div className={s.form}>
              {start === 'chat' && (
                <section className={s.card} aria-label={t('jobNew.a11y2')}>
                  <div className={s.head}>
                    <IconSparkles size={18} aria-hidden="true" />
                    <h2 className={s.h}>{t('jobNew.chatMsg')}</h2>
                    <span role="img" aria-label={t('jobNew.a11y3')} title={t('jobNew.a11y4')} className={s.tip}>
                      <IconInfoCircle size={14} aria-hidden="true" />
                    </span>
                    <button type="button" className={s.fold} aria-expanded={!shut.chat} aria-label={t('jobNew.a11y5')} onClick={() => toggle('chat')}>
                      <IconChevronDown size={18} aria-hidden="true" style={{ transform: shut.chat ? undefined : 'rotate(180deg)' }} />
                    </button>
                  </div>
                  {!shut.chat && (
                    <div className={s.chat}>
                      <textarea className={s.chatMsg} aria-label={t('jobNew.a11y6')} rows={3} value={chatMsg} onChange={(e) => setChatMsg(e.target.value)} />
                      {chatP && chatChips(chatP).length > 0 && (
                        <div className={s.chips}>
                          {chatChips(chatP).map((c) => (
                            <span key={c} className={s.chip}>{c}</span>
                          ))}
                        </div>
                      )}
                      {chatDups.length > 0 && (
                        <div role="alert" className={s.dupBox}>
                          <b>{t('jobNew.dupHit')}</b>
                          {chatDups.map((d) => (
                            <div key={d.key + d.job} className={s.dupRow}>
                              <span className={d.hard ? s.dupHard : s.dupSoft}>{d.key}</span>
                              <span className={s.dupVal}>{d.val} · {d.job} · {d.ss}</span>
                              <a href={`/ezy/jobs/${d.job}`} className={s.dupLink}><IconExternalLink size={16} aria-hidden="true" />Mở {d.job} ↗</a>
                            </div>
                          ))}
                        </div>
                      )}
                      {chatP && chatDups.length === 0 && <div className={s.noDup}>{t('jobNew.noDup')}</div>}
                      <div className={s.chatAct}>
                        <Button variant="secondary" icon={<IconFileSearch size={16} aria-hidden="true" />} onClick={chatRun}>{t('jobNew.readCheck')}</Button>
                        <Button variant="secondary" icon={<IconExternalLink size={16} aria-hidden="true" />} onClick={chatFill}>{t('jobNew.openFilled')}</Button>
                      </div>
                    </div>
                  )}
                </section>
              )}
              {isMulti && (
                <section className={s.card} aria-label="Multi Book">
                  <div className={s.head}>
                    <IconPackage size={18} aria-hidden="true" />
                    <h2 className={s.h}>{t('jobNew.multi')}</h2>
                    <span role="img" aria-label={t('jobNew.a11y7')} title={t('jobNew.a11y8')} className={s.tip}>
                      <IconInfoCircle size={14} aria-hidden="true" />
                    </span>
                    <span className={s.mbSum}>{mbTotal ? `${mbTotal} booking · ${Object.values(mbN).filter((x) => x > 0).length} chuyến` : 'chọn chuyến và số booking'}</span>
                    <button type="button" className={s.fold} aria-expanded={!shut.mb} aria-label={t('jobNew.a11y9')} onClick={() => toggle('mb')}>
                      <IconChevronDown size={18} aria-hidden="true" style={{ transform: shut.mb ? undefined : 'rotate(180deg)' }} />
                    </button>
                  </div>
                  {!shut.mb && (
                    <div className={s.mb}>
                      <div className={s.mbSeg} role="radiogroup" aria-label={t('jobNew.a11y10')}>
                        {MB_SRC.map((o) => (
                          <button key={o.key} type="button" role="radio" aria-checked={mbSrc === o.key} className={`${s.mbSegBtn} ${mbSrc === o.key ? s.mbSegOn : ''}`} onClick={() => setMbSrc(o.key)}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                      <table className={s.mbTbl}>
                        <thead>
                          <tr>
                            <th className={s.mbChk} />
                            <th>{t('jobNew.carrierVessel')}</th>
                            <th>ETD</th>
                            <th>Cut-off CY</th>
                            <th>Service</th>
                            <th className={s.mbQty}>{t('jobNew.bkQty')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {MB.map(([car, vsl, etd, cut, svc, soon], i) => {
                            const n = mbN[i] ?? 0;
                            return (
                              <tr key={i} className={n > 0 ? s.mbOn : undefined}>
                                <td className={s.mbChk}>
                                  <input type="checkbox" aria-label={`Chọn ${vsl}`} checked={n > 0} onChange={() => mbSet(i, n > 0 ? 0 : 1)} />
                                </td>
                                <td><b>{car}</b> · {vsl}</td>
                                <td>{etd}</td>
                                <td className={soon ? s.mbSoon : s.mbCut}>{cut}</td>
                                <td className={s.mbSvc}>{svc}</td>
                                <td className={s.mbQty}>
                                  <div className={s.mbStep}>
                                    <button type="button" aria-label={t('jobNew.a11y11')} onClick={() => mbSet(i, n - 1)}>−</button>
                                    <b>{n}</b>
                                    <button type="button" aria-label={t('jobNew.a11y12')} onClick={() => mbSet(i, n + 1)}>+</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}
              {sections.map((sec) => {
                const Ico = SEC_ICON[sec.key] ?? IconPackage;
                const miss = missing.filter((f) => sec.fields.some((x) => x.key === f.key)).length;
                const open = !shut[sec.key];
                return (
                  <section key={sec.key} className={s.card} aria-label={sec.label}>
                    <div className={s.head}>
                      <Ico size={18} aria-hidden="true" />
                      <h2 className={s.h}>{sec.label}</h2>
                      {miss > 0 && <span className={s.miss}>{miss} thiếu</span>}
                      <button type="button" className={s.fold} aria-expanded={open} aria-label={t('jobNew.a11y13')} onClick={() => toggle(sec.key)}>
                        <IconChevronDown size={18} aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : undefined }} />
                      </button>
                    </div>
                    {open && <div className={s.grid}>{sec.fields.map(field)}</div>}
                  </section>
                );
              })}
              <section className={s.card} aria-label={t('jobNew.a11y14')}>
                <div className={s.head}>
                  <IconFileText size={18} aria-hidden="true" />
                  <h2 className={s.h}>{t('jobNew.attach')}</h2>
                  <span role="img" aria-label={t('jobNew.a11y15')} title={t('jobNew.a11y16')} className={s.tip}>
                    <IconInfoCircle size={14} aria-hidden="true" />
                  </span>
                  <button type="button" className={s.fold} aria-expanded={!shut.att} aria-label={t('jobNew.a11y17')} onClick={() => toggle('att')}>
                    <IconChevronDown size={18} aria-hidden="true" style={{ transform: shut.att ? undefined : 'rotate(180deg)' }} />
                  </button>
                </div>
                {!shut.att && (
                  <div className={s.chat}>
                    <div className={s.chatAct}>
                      <span>{t('jobNew.drop')}</span>
                      <Button variant="secondary" icon={<IconUpload size={16} aria-hidden="true" />} onClick={() => toast({ msg: 'Chưa mở ở bản này' })}>{t('jobNew.pick')}</Button>
                    </div>
                  </div>
                )}
              </section>
            </div>
            <aside className={s.side} aria-label={t('jobNew.a11y18')}>
              <section className={s.card} aria-label={t('jobNew.sum')}>
                <div className={s.head}><h2 className={s.h}>{t('jobNew.sum')}</h2><button type="button" className={s.fold} aria-expanded={!shut.sum} aria-label={t('jobNew.a11y19')} onClick={() => toggle('sum')}>
                    <IconChevronDown size={18} aria-hidden="true" style={{ transform: shut.sum ? undefined : 'rotate(180deg)' }} />
                  </button></div>
                {!shut.sum && <dl className={s.sum}>
                  {summary.map(([k, val]) => (
                    <div key={k}>
                      <dt>{k}</dt>
                      <dd>{val || '—'}</dd>
                    </div>
                  ))}
                </dl>}
              </section>
              <section className={s.card} aria-label={t('jobNew.a11y20')}>
                <div className={s.head}><h2 className={s.h}>{t('jobNew.chk')}</h2><button type="button" className={s.fold} aria-expanded={!shut.chk} aria-label={t('jobNew.a11y21')} onClick={() => toggle('chk')}>
                    <IconChevronDown size={18} aria-hidden="true" style={{ transform: shut.chk ? undefined : 'rotate(180deg)' }} />
                  </button></div>
                {!shut.chk && <div className={`${s.check} ${missing.length ? s.checkWarn : s.checkOk}`}>
                  {missing.length ? <IconCircleDashed size={16} aria-hidden="true" /> : <IconCheck size={16} aria-hidden="true" />}
                  <span>
                    {missing.length
                      ? `Thiếu ${missing.length} ô: ${missing.slice(0, 3).map((m) => m.label).join(', ')}${missing.length > 3 ? '…' : ''}`
                      : 'Đủ ô bắt buộc'}
                  </span>
                </div>}
              </section>
              <section className={s.card} aria-label={t('jobNew.a11y22')}>
                <div className={s.head}><h2 className={s.h}>{t('jobNew.after')}</h2><button type="button" className={s.fold} aria-expanded={!shut.aft} aria-label={t('jobNew.a11y23')} onClick={() => toggle('aft')}>
                    <IconChevronDown size={18} aria-hidden="true" style={{ transform: shut.aft ? undefined : 'rotate(180deg)' }} />
                  </button></div>
                {!shut.aft && <dl className={s.after}>
                  {afterOf(kind).map(([code, k]) => (
                    <div key={code}>
                      <dt>{code}</dt>
                      <dd>{k}</dd>
                    </div>
                  ))}
                </dl>}
              </section>
            </aside>
          </div>
        </StateView>
      </div>
      {state === 'ready' && (
        <footer className={s.foot}>
          <span className={missing.length ? s.footWarn : s.footOk}>{missing.length ? `Thiếu ${missing.length} ô bắt buộc` : 'Đủ thông tin để tạo'}</span>
          <Button variant="secondary" icon={<IconDeviceFloppy size={16} aria-hidden="true" />} onClick={noWrite}>
            {t('jobNew.saveDraft')}
          </Button>
        </footer>
      )}
    </>
  );
}
